import { Box, Button, Typography } from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import api from '../api'
import {
  flushPendingResults, pendingResultsCount, subscribePendingResults
} from '../offlineMode/pendingResults'
import { IAlphaprocessState } from '../store'

const RETRY_MS = 20000

// Resends answers that could not be saved (offlineMode/pendingResults) and shows how many are
// still waiting. Runs everywhere, also for shared links without an account.
export default function PendingResultsSync() {
  const { t } = useTranslation()
  const userId = useSelector((state: IAlphaprocessState) => state.core.user?.id ?? null)
  const isOffline = useSelector((state: IAlphaprocessState) => !!state.core.isOffline)
  const [count, setCount] = useState(pendingResultsCount())
  const [busy, setBusy] = useState(false)

  useEffect(() => subscribePendingResults(setCount), [])

  const flush = useCallback(async (manual = false) => {
    if (pendingResultsCount() === 0) return
    setBusy(true)
    try {
      const before = pendingResultsCount()
      const { sent, rejected } = await flushPendingResults((entry) => api.results.sendPending(entry), userId)
      const left = pendingResultsCount()
      if (sent > 0 && left === 0) toast(t('pendingResults.allSaved'), { type: 'success' })
      if (rejected > 0) toast(t('pendingResults.rejected', { count: rejected }), { type: 'error', autoClose: false })
      if (manual && left === before) toast(t('pendingResults.stillOffline'), { type: 'warning' })
    } finally {
      setBusy(false)
    }
  }, [t, userId])

  // on start, when the connection or the user changes, and every 20 seconds while answers wait
  useEffect(() => { flush() }, [flush, isOffline])
  useEffect(() => {
    const onOnline = () => { flush() }
    window.addEventListener('online', onOnline)
    return () => window.removeEventListener('online', onOnline)
  }, [flush])
  useEffect(() => {
    if (count === 0) return undefined
    const timer = window.setInterval(() => { flush() }, RETRY_MS)
    return () => window.clearInterval(timer)
  }, [count, flush])

  // visitors without account see the status line of the process instead (VisitorSaveStatus)
  if (count === 0 || !userId) return null

  return (
    <Box
      role="status"
      sx={{
        position: 'fixed',
        left: { xs: 12, sm: 20 },
        right: { xs: 12, sm: 'auto' },
        bottom: { xs: 'calc(96px + env(safe-area-inset-bottom))', sm: 20 },
        zIndex: 1400,
        maxWidth: 420,
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        px: 2,
        py: 1.25,
        borderRadius: '12px',
        bgcolor: '#FFF4E0',
        border: '1px solid #F0D9A8',
        boxShadow: '0 4px 16px rgba(16,24,40,.18)',
        color: '#6B4200'
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 14, fontWeight: 700 }}>{t('pendingResults.title', { count })}</Typography>
        <Typography sx={{ fontSize: 12.5 }}>{t('pendingResults.hint')}</Typography>
      </Box>
      <Button
        size="small"
        variant="outlined"
        disabled={busy}
        onClick={() => flush(true)}
        sx={{
          textTransform: 'none', fontWeight: 700, color: '#6B4200', borderColor: '#D9B56E', whiteSpace: 'nowrap', '&:hover': { borderColor: '#B58A3A' }
        }}
      >
        {busy ? t('pendingResults.sending') : t('pendingResults.sendNow')}
      </Button>
    </Box>
  )
}
